import React from 'react';
import { Form, FormOnSubmit } from 'components/form';
import { useStartAnonymousPhoneVerificationMutation } from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingStepKey,
  SelfOnboardingStepProps,
  SelfOnboardingVerificationPhoneNumberFormData,
} from 'containers/selfOnboarding/steps/types';
import {
  StepInfo,
  StepActions,
  StepInfoTitle,
} from 'containers/selfOnboarding/steps/components';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import { saveVerificationPhoneNumber } from 'containers/selfOnboarding/helpers/verificationPhoneStorage';
import {
  getDefaultValues,
  getFormInputs,
} from 'containers/selfOnboarding/steps/loginStartStep/LoginStartStep.forms';
import { getPhoneNumberAsE164 } from 'utils';

/**
 * Authenticated-flow phone entry (pre-account). Everyone verifies their phone up
 * front via the accountless OTP; there is no "no existing account" bounce and no
 * skip. Carries zero jobsite display data — nothing jobsite-specific is shown
 * before the worker authenticates.
 */
export function AuthLoginStartStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const { state, updateState, navigation, stepConfig, localize, language } =
    props;
  const { verificationPhoneNumber, basicInfo } = state;
  const phoneNumber = verificationPhoneNumber ?? basicInfo?.phoneNumber;
  const { goToStep, getStepNumberInfo, state: navigationState } = navigation;
  const { isReview, autoFocusField } = navigationState;
  const stepNumberInfo = getStepNumberInfo();

  const [isSaving, setIsSaving] = React.useState(false);
  const [startAnonymousPhoneVerification] =
    useStartAnonymousPhoneVerificationMutation();

  const onSubmit: FormOnSubmit<
    SelfOnboardingVerificationPhoneNumberFormData
  > = async (data, event): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      updateState({
        ...data,
        basicInfo: { ...basicInfo, phoneNumber: data.verificationPhoneNumber },
      });
      // Persist so "request another code" survives a reload on the OTP step,
      // where in-memory flow state (but not the URL step) is wiped.
      saveVerificationPhoneNumber(data.verificationPhoneNumber);
      const e164PhoneNumber = getPhoneNumberAsE164(
        data.verificationPhoneNumber,
      );
      const result = await startAnonymousPhoneVerification({
        variables: { input: { phoneNumber: e164PhoneNumber } },
      });
      setIsSaving(false);
      if (result.data.startAnonymousPhoneVerification.success) {
        goToStep(SelfOnboardingStepKey.ConfirmLoginCode);
      }
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  const inputs = React.useMemo(() => getFormInputs(), []);
  const defaultValues = React.useMemo(
    () => getDefaultValues({ verificationPhoneNumber: phoneNumber }),
    [phoneNumber],
  );
  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={localize(Copy.self_onboarding_login_header)}
          subtitle={localize(Copy.self_onboarding_login_instructions)}
          stepNumberInfo={stepNumberInfo}
          withBottomBorder
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingVerificationPhoneNumberFormData) ??
          true
        }
        renderBelow={
          <StepActions
            localize={localize}
            isReview={isReview}
            actions="continue"
            continueActionWithSpinner={isSaving}
            ignoreJobsiteConfigSkipFlag
            showSmsLiteAcknowledgement
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
